<template>
  <b-modal
    id="add-or-update-modal"
    :visible="isAddActive"
    centered
    hide-footer
    hide-header
    no-close-on-esc
    no-close-on-backdrop
    size="lg"
    @hidden="resetForm"
    @hide="resetForm"
    @change="(val) => $emit('update:is-add-active', val)"
  >
    <template #default="{ hide }">
      <b-overlay :show="loading" class="px-2">
        <div class="d-flex justify-content-between align-items-center content-sidebar-header py-1" style="border-bottom: 1px solid #000000;">
          <h5 class="mb-0" style="font-weight: 400; font-size: 14px; line-height: 160%;">
            {{ isUpdate ? 'Update' : 'Add' }} {{ modalTitle }}
          </h5>

          <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
        </div>

        <validation-observer
          #default="{ handleSubmit, invalid }"
          ref="update_modal_form"
        >
          <b-form class="my-1" @submit.prevent="handleSubmit(onCreateOrUpdate)" @reset.prevent="resetForm">
            <b-alert v-if="errorMessage" variant="danger" :show="true">
              <div class="alert-body font-small-2">
                <b-card-text class="mb-0">
                  {{ errorMessage }}
                </b-card-text>
              </div>
            </b-alert>
            
            <b-row>
              <b-col cols="12" md="12">
                <validation-provider #default="validationContext" name="Question" rules="required">
                  <b-form-group label-for="question">
                    <template #label>
                      <span>Question<span style="color: tomato">*</span></span>
                    </template>

                    <b-form-input
                      id="question"
                      v-model="form_data.question"
                      placeholder="Question"
                      :disabled="disableForm"
                      :state="getValidationState(validationContext)"
                    />

                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <b-col cols="12" md="3">
                <validation-provider #default="validationContext" name="Answer Type" rules="">
                  <b-form-group label-for="description">
                    <template #label>
                      <span>Answer Type<span style="color: tomato">*</span></span>
                    </template>
                    
                    <v-select 
                      v-model="form_data.answer_type" 
                      :options="answerTypes" 
                      :close-on-select="true" 
                      :reduce="val => val.value" 
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" 
                      :clearable="false" 
                      label="label" 
                    />

                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <b-col cols="12" md="6">
                <validation-provider #default="validationContext" name="Related Complaints" rules="">
                  <b-form-group label-for="description">
                    <template #label>
                      <span>Related Complaints<span style="color: tomato">*</span></span>
                    </template>
                    
                    <common-complaints-select 
                      :values="form_data.related_complaint_ids"
                      @change="val => form_data.related_complaint_ids = val"
                    />

                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <b-col v-if="form_data.answer_type === 'select'" cols="12" md="12">
                <validation-provider #default="validationContext" name="Answer Type" rules="">
                  <b-form-group label-for="description">
                    <template #label>
                      <span>Select Options<span style="color: tomato">*</span></span>
                    </template>
                    
                    <v-select 
                      v-model="form_data.select_options" 
                      :options="form_data.select_options" 
                      :multiple="true"
                      :taggable="true"
                      :filterable="false" 
                      :clearable="true" 
                      :close-on-select="true"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" 
                    >
                      <template #header>
                        <div style="opacity: 0.8">Type a new option and press enter</div>
                      </template>

                      <template #no-options>
                        No options added. Type a new option and press enter
                      </template>
                    </v-select>

                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
            </b-row>

            <!-- Form Actions -->
            <div class="d-flex mt-2">
              <b-button
                v-if="!isUpdate"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="mr-2"
                type="button"
                :disabled="invalid || disableForm"
                @click="onCreateOrUpdate(true)"
              >
                Save & Add New
              </b-button>

              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="outline-primary"
                class="mr-2"
                type="submit"
                :disabled="invalid || disableForm"
              >
                {{ editingData ? 'Update' : 'Save' }} & Exit
              </b-button>

              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="button"
                variant="outline-danger"
                @click="hide"
              >
                Cancel
              </b-button>
            </div>
          </b-form>
        </validation-observer>
      </b-overlay>
    </template>
  </b-modal>
</template>

<script>
import {
  BOverlay, BSidebar, BForm, BFormGroup, BFormInput, 
  BFormTextarea, BFormInvalidFeedback, BButton, BFormSelect,
  BAlert, BCardText, BModal, BRow, BCol, BFormDatepicker
} from 'bootstrap-vue'

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import CommonComplaintsSelect from "@/@core/components/shared/admin/selects/CommonComplaintsSelect.vue"

import { required } from '@validations'
import { get, cloneDeep } from 'lodash';
import { ValidationProvider, ValidationObserver } from 'vee-validate'

import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'

const rawData = {
  question: "",
  answer_type: 'checkbox',
  select_options: [],
  related_complaint_ids: []
}

export default {
  components: {
    BRow,
    BCol,
    BForm,
    BModal,
    BAlert,
    BButton,
    vSelect,
    BOverlay,
    BSidebar,
    BCardText,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BFormDatepicker,
    BFormInvalidFeedback,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
    CommonComplaintsSelect
  },
  directives: {
    Ripple,
  },
  model: {
    prop: 'isAddActive',
    event: 'update:is-add-active',
  },
  props: {
    resource: {
      type: String,
      required: true
    },
    isAddActive: {
      type: Boolean,
      required: true,
    },
    editingData: {
      type: [Object, null],
      required: false,
      default: () => null
    },
    modalTitle: {
      type: String,
      default: 'Record'
    }
  },
  data() {
    return {
      required,
      loading: false,
      errorMessage: "",

      answerTypes: [
        {
          label: 'Yes/No',
          value: 'checkbox'
        },
        {
          label: 'Text',
          value: 'text'
        },
        {
          label: 'Select',
          value: 'select'
        }
      ],
      commonComplaints: [],
      form_data: cloneDeep(rawData),
    }
  },
  computed: {
    isUpdate(){
      return !!this.editingData
    },
    disableForm(){
      return this.editingData ? !this.can(`${this.resource}:CanUpdate`) : !this.can(`${this.resource}:CanAddNew`)
    },
  },
  watch: {
    editingData: {
      handler(v){
        if (v){
          this.form_data = v
        } else {
          this.form_data = cloneDeep(rawData)
        }
      }, 
      immediate: true,
      deep: true
    }
  },
  methods: {
    async onCreateOrUpdate(save_and_new = false){
      try {
        this.loading = true;
        this.errorMessage = "";

        const success = await this.$refs.update_modal_form.validate();
        if (!success){
          return;
        }

        if (!this.isUpdate && !this.can(`${this.resource}:CanAddNew`)) {
          return
        }

        if (this.isUpdate && !this.can(`${this.resource}:CanUpdate`)) {
          return
        }

        const { id, question, answer_type, select_options, related_complaint_ids } = this.form_data;
        if (this.isUpdate){
          this.$emit('update', {
            id,
            payload: {
              question, answer_type, select_options, related_complaint_ids
            }
          });
        } else {
          this.$emit('create', {
            payload: {
              question, answer_type, select_options, related_complaint_ids
            },
            save_and_new
          });
        }
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    resetForm() {
      this.form_data = cloneDeep(rawData)
      if (this.$refs.update_modal_form) {
        this.$refs.update_modal_form.reset()
      }
    }
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
